import { FunctionComponent, useEffect, useMemo } from 'react';
import { Customer, SingleSite } from '@/core/entities';
import { ALL_SITES_ID, useAllCycles } from '@/queries/logs';
import { Box } from '@mui/system';
import useToastMessage from '@/shared/context/toast-message-context';
import { AxiosError } from 'axios';
import { ApiError } from '@/shared/types/api-errors';
import { QueryDateRange } from '../../types';
import DashboardItem from '../../DashboardItem';
import AllCyclesChart from './charts';
import { SelectedLocation } from '../completed-cycles/filters/LocationFiltersDialog';

const BASE_TITLE = 'Cycles by ';

interface AllCyclesProps {
	site?: SingleSite;
	locationsFilter?: SelectedLocation[];
	locationId?: string;
	customer?: Pick<Customer, 'id' | 'name'>;
	showAllSitesData: boolean;
	exportType: string | null;
	isAdmin: boolean;
	dateRange: QueryDateRange;
	onChartReady?: (ready: boolean) => void;
}

const AllCycles: FunctionComponent<AllCyclesProps> = ({
	site,
	locationId,
	customer,
	showAllSitesData,
	locationsFilter,
	dateRange,
	exportType,
	onChartReady,
}) => {
	const { dispatch } = useToastMessage();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const params: Record<string, any> = {
		fromDate: dateRange.start,
		toDate: dateRange.end,
	};

	if (customer) {
		params.customerId = customer.id;
	}

	if (site) {
		params.siteId = site.id;
	}

	if (locationId) {
		params.locationId = locationId;
	}

	const {
		data: allCyclesData,
		error,
		isFetching: isFetchingCycles,
	} = useAllCycles(params, showAllSitesData ? ALL_SITES_ID : site?.id);

	const isLoading = useMemo(() => isFetchingCycles, [isFetchingCycles]);

	useEffect(() => {
		const { message, statusCode } = (error as AxiosError<ApiError>)?.response?.data || {};
		if (statusCode === 400) {
			dispatch({
				type: 'error',
				content: message,
			});
		}
	}, [error, dispatch]);

	const title = useMemo(() => {
		const getTitle = () => {
			if (!customer) {
				return `${BASE_TITLE}Customers`;
			}

			if (locationsFilter?.length === 0 && customer) {
				return `${BASE_TITLE}Site`;
			}

			const allHaveLocation =
				locationsFilter && locationsFilter.every((item) => item.location !== undefined);
			if (allHaveLocation) {
				return BASE_TITLE + (locationsFilter[locationsFilter.length - 1]?.level?.name ?? '');
			}

			const firstLevelWithoutLocation = locationsFilter?.find(
				(item) => item.location === undefined
			);
			return BASE_TITLE + (firstLevelWithoutLocation?.level?.name ?? '');
		};
		return (
			<>
				<Box component="span" sx={{ displayPrint: 'none' }}>
					{getTitle()}
				</Box>
				<Box component="span" sx={{ display: 'none', displayPrint: 'inline' }}>
					{getTitle()}
					{site?.name && ` - ${site.name}`}
				</Box>
			</>
		);
	}, [locationsFilter, site, customer]);

	return (
		<DashboardItem title={title} sx={{ overflow: 'hidden' }}>
			<AllCyclesChart
				allCycles={allCyclesData}
				dateRange={dateRange}
				exportType={exportType}
				onChartReady={onChartReady}
				isLoading={isLoading}
			/>
		</DashboardItem>
	);
};

export default AllCycles;
